class BeyondMobileMenu extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.trigger = this.querySelector('.beyond-mobile-trigger');
    this.panel = this.querySelector('.beyond-mobile-panel');
    this.overlay = this.querySelector('.beyond-mobile-overlay');
    this.closeButton = this.querySelector('.beyond-mobile-close');
    this.trigger.addEventListener('click', () => this.panel.hidden ? this.open() : this.close(), options);
    this.closeButton.addEventListener('click', () => this.close(), options);
    this.overlay.addEventListener('click', () => this.close(), options);
    this.panel.addEventListener('click', event => {
      if (event.target.closest('.beyond-mobile-account')) this.close(false);
    }, options);
    this.panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation(); this.close(); return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href],button,summary')].filter(el => el.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, options);
    this.panel.addEventListener('toggle', event => {
      if (!event.target.matches('details')) return;
      event.target.querySelector('summary').setAttribute('aria-expanded', String(event.target.open));
      const category = event.target;
      if (!category.matches('.beyond-mobile-category') || !category.open) return;
      this.panel.querySelectorAll('.beyond-mobile-category[open]').forEach(other => {
        if (other !== category) {
          other.open = false;
          other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      });
      cancelAnimationFrame(this.menuScrollFrame);
      this.menuScrollFrame = requestAnimationFrame(() => {
        if (this.panel.hidden || !category.open) return;
        // Keep the clicked heading in place. Only recover it if collapsing an
        // earlier section moved it behind the sticky drawer heading.
        const heading = this.panel.querySelector('.beyond-mobile-heading');
        const visibleTop = this.panel.getBoundingClientRect().top + (heading?.offsetHeight || 0) + 16;
        const categoryTop = category.getBoundingClientRect().top;
        if (categoryTop < visibleTop) {
          this.panel.scrollTo({ top: Math.max(0, this.panel.scrollTop + categoryTop - visibleTop), behavior: 'instant' });
        }
      });
    }, { ...options, capture: true });
    this.panel.querySelectorAll('summary').forEach(summary => summary.setAttribute('aria-expanded', 'false'));
    document.addEventListener('pointerdown', event => {
      if (!this.panel.hidden && !this.contains(event.target)) this.close(false);
    }, options);
    window.addEventListener('resize', () => {
      if (innerWidth >= 990) this.close(false);
      else if (!this.panel.hidden) this.updatePosition();
    }, options);
    window.addEventListener('pageshow', event => { if (event.persisted) this.close(false); }, options);
  }
  updatePosition() {
    const bottom = this.closest('.header-wrapper').getBoundingClientRect().bottom;
    this.style.setProperty('--beyond-menu-top', Math.max(0, bottom) + 'px');
  }
  open() {
    this.updatePosition();
    this.panel.hidden = false;
    this.overlay.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('beyond-mobile-open');
    this.closest('.header-wrapper').preventHide = true;
    this.inertElements = [...document.querySelectorAll('main,footer')].map(el => [el, el.inert]);
    this.inertElements.forEach(([el]) => { el.inert = true; });
    this.closeButton.focus();
  }
  close(restoreFocus = true) {
    if (!this.panel || this.panel.hidden) return;
    cancelAnimationFrame(this.menuScrollFrame);
    this.panel.querySelectorAll('beyond-brand-carousel').forEach(carousel => carousel.reset?.());
    this.panel.hidden = true;
    this.overlay.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('beyond-mobile-open');
    const header = this.closest('.header-wrapper');
    if (header) header.preventHide = false;
    this.inertElements?.forEach(([el, inert]) => { el.inert = inert; });
    this.inertElements = [];
    if (restoreFocus) this.trigger.focus();
  }
  disconnectedCallback() { this.close(false); this.controller?.abort(); }
}
if (!customElements.get('beyond-mobile-menu')) customElements.define('beyond-mobile-menu', BeyondMobileMenu);

if (!customElements.get('beyond-desktop-category')) customElements.define('beyond-desktop-category', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const opts = {signal:this.abort.signal};
    this.details = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    const close = () => { this.details.open = false; };
    const open = () => {
      document.querySelectorAll('beyond-desktop-category details[open]').forEach(d => {if(d !== this.details)d.open=false;});
      this.details.open = true;
    };
    this.addEventListener('pointerenter', e => {if(e.pointerType==='mouse'){clearTimeout(this.timer);open();}},opts);
    this.addEventListener('pointerleave', e => {if(e.pointerType==='mouse')this.timer=setTimeout(close,180);},opts);
    this.details.addEventListener('toggle', () => {
      this.toggle.setAttribute('aria-expanded',String(this.details.open));
      const video = this.querySelector('.beyond-lessons-video');
      if (video) {
        if (!this.details.open) video.pause();
        else {
          if (!video.getAttribute('src')) video.src = video.dataset.src;
          if (!matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData) video.play().catch(() => {});
        }
      }
      if(this.details.open)document.querySelectorAll('beyond-desktop-category details[open]').forEach(d=>{if(d!==this.details)d.open=false;});
    },opts);
    this.toggle.setAttribute('aria-expanded','false');
    this.addEventListener('keydown', e => {
      if(e.key==='Escape' && this.details.open){e.preventDefault();close();this.querySelector('a').focus();}
      if(e.key==='ArrowDown' && (e.target===this.toggle || e.target===this.querySelector('a'))){e.preventDefault();open();this.querySelector('.beyond-category-panel a')?.focus();}
    },opts);
    this.addEventListener('pointerdown', e=>{
      if(e.button!==0 || !e.target.closest('.beyond-category-panel'))return;
      if(!e.target.closest('a,button,input,select,textarea,summary,[tabindex]'))e.preventDefault();
    },opts);
    this.addEventListener('focusout', e=>{if(!this.contains(e.relatedTarget))close();},opts);
    document.addEventListener('pointerdown', e=>{if(!this.contains(e.target))close();},opts);
    window.addEventListener('resize',()=>{if(innerWidth<990)close();},opts);
  }
  disconnectedCallback(){clearTimeout(this.timer);this.abort?.abort();}
});

if (!customElements.get('beyond-brand-carousel')) customElements.define('beyond-brand-carousel', class extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();this.abort=new AbortController();
    const track=this.querySelector('.beyond-brand-carousel-track');
    this.track = track;
    this.closest('details')?.addEventListener('toggle', () => this.reset(), {signal:this.abort.signal});

    const controls=this.querySelector('.brand-desktop-controls');
    const back=this.querySelector('[data-brand-back]'),more=this.querySelector('[data-brand-more]');
    // Keep focus inside the menu when repeat clicks land in an empty arrow slot.
    controls.addEventListener('pointerdown',event=>{
      if(event.button!==0)return;
      event.preventDefault();
      const button=event.target.closest('button');
      if(button && !button.disabled)button.focus({preventScroll:true});
    },{signal:this.abort.signal});
    const update=()=>{
      if(!track.clientWidth)return;
      const atStart=track.scrollLeft<=4,atEnd=track.scrollLeft+track.clientWidth>=track.scrollWidth-4;
      controls.hidden=track.scrollWidth<=track.clientWidth+4;
      if(atStart && document.activeElement===back && !atEnd)more.focus({preventScroll:true});
      if(atEnd && document.activeElement===more && !atStart)back.focus({preventScroll:true});
      back.disabled=atStart;more.disabled=atEnd;
    };
    for(const [button,direction] of [[back,-1],[more,1]])button.addEventListener('click',()=>{
      track.scrollBy({left:direction*track.clientWidth,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    },{signal:this.abort.signal});
    track.addEventListener('scroll',update,{signal:this.abort.signal,passive:true});
    this.observer=new ResizeObserver(update);this.observer.observe(track);update();
    track.addEventListener('focusin',event=>{
      const item=event.target.closest('li');if(!item)return;
      const box=track.getBoundingClientRect(),rect=item.getBoundingClientRect();
      if(rect.left<box.left || rect.right>box.right)track.scrollBy({left:rect.left-box.left,behavior:'instant'});
    },{signal:this.abort.signal});
  }
  reset(){
    this.track?.scrollTo({left:0,behavior:'instant'});
    const back=this.querySelector('[data-brand-back]'),more=this.querySelector('[data-brand-more]');
    if(back)back.disabled=true;
    if(more && this.track?.clientWidth)more.disabled=this.track.scrollWidth<=this.track.clientWidth+4;
  }
  disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();}
});

/* Let photography, but not product controls/text, pass behind a translucent header. */
(() => {
  const main = document.querySelector('#MainContent');
  const header = document.querySelector('.section-header');
  if (!main || !header || !('IntersectionObserver' in window)) return;
  const selector = '.card-wrapper .card__content, .card-wrapper .card-product-option';
  const visible = new Set();
  const originalClips = new WeakMap();
  let frame = 0;
  const restore = element => {
    if (originalClips.has(element)) {
      element.style.clipPath = originalClips.get(element);
      originalClips.delete(element);
    }
  };
  const update = () => {
    frame = 0;
    const headerBox = header.getBoundingClientRect();
    const edge = headerBox.bottom > 0 && headerBox.top < window.innerHeight ? headerBox.bottom : 0;
    const measurements = [...visible].map(element => [element, element.getBoundingClientRect()]);
    measurements.forEach(([element, box]) => {
      if (!element.isConnected) { visible.delete(element); return; }
      const overlap = Math.min(box.height, Math.max(0, edge - box.top));
      if (overlap > 0 && box.height > 0) {
        if (!originalClips.has(element)) originalClips.set(element, element.style.clipPath);
        element.style.clipPath = `inset(${Math.ceil(overlap)}px -100px 0 -100px)`;
      } else restore(element);
    });
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible.add(entry.target);
      else { visible.delete(entry.target); restore(entry.target); }
    });
    schedule();
  }, { rootMargin: '200px 0px 0px 0px' });
  const observed = new WeakSet();
  const watch = element => {
    if (!observed.has(element)) { observed.add(element); observer.observe(element); }
  };
  const mediaSelector = 'img,picture,video,canvas,iframe';
  const register = root => {
    if (root.matches?.(selector)) watch(root);
    root.querySelectorAll?.(selector).forEach(watch);
    // A registration pass is synchronous. Reuse subtree/media and ancestor
    // results within this pass, then discard them before later DOM mutations.
    const mediaCache = new WeakMap();
    const targetCache = new WeakMap();
    const containsMedia = element => {
      if (!mediaCache.has(element)) mediaCache.set(element, !!element.querySelector(mediaSelector));
      return mediaCache.get(element);
    };
    const resolveTarget = element => {
      if (targetCache.has(element)) return targetCache.get(element);
      const chain = [];
      let target = element;
      while (true) {
        if (targetCache.has(target)) { target = targetCache.get(target); break; }
        chain.push(target);
        const parent = target.parentElement;
        if (!parent || parent === main ||
          parent.matches('body,header,.section-header,footer') || containsMedia(parent)) break;
        target = parent;
      }
      chain.forEach(item => targetCache.set(item, target));
      return target;
    };
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent.trim()) continue;
      const parent = node.parentElement;
      if (!parent || parent.closest('script,style,noscript,svg,textarea,select,option')) continue;
      const target = resolveTarget(parent);
      if (target !== main && !containsMedia(target)) watch(target);
    }
  };
  register(main);
  document.querySelectorAll('.beyond-sale-notice__link,footer').forEach(register);
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) register(node); }));
    schedule();
  }).observe(main, { childList: true, subtree: true });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  new ResizeObserver(schedule).observe(header);
})();
